/**
 * Account Detail Page
 *
 * Displays account profile, contacts, services, orders, and placeholder tabs for assets, billing, cases, tickets.
 * - Profile: Edit button opens AccountFormDialog
 * - Contacts: Table layout; add/edit/delete; primary contact swap when promoting secondary to primary
 * - Services: Grouped by type (NBN, Sonweb, etc.); add/edit with type-specific fields
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  Tooltip,
  Menu,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Person as ProfileIcon,
  Contacts as ContactsIcon,
  Dns as ServicesIcon,
  Inventory as AssetsIcon,
  Receipt as AccountingIcon,
  Assignment as CasesIcon,
  ConfirmationNumber as TicketsIcon,
  Checklist as TasksIcon,
  ShoppingCart as OrdersIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  DriveFileMove as MoveServiceIcon,
  Email as CommunicationsIcon,
  StickyNote2 as NotesIcon,
  FolderOutlined as DocumentsIcon,
  Star as StarFilledIcon,
  StarBorder as StarBorderIcon,
  OpenInNew as OpenInNewIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as AddressIcon,
  Notifications as NotificationsIcon,
  Badge as AccountNumberIcon,
  Business as BusinessIcon,
  StarRate as ReviewsIcon,
  ManageAccounts as StatusIcon,
  Fingerprint as AbnIcon,
  Domain as OrganisationIcon,
  ReportProblem as ServiceOutageIcon,
  Schedule as PlannedOutageIcon,
} from '@mui/icons-material';
import { format as formatDate } from 'date-fns';
import { Account, AccountContact, AccountContactCreate, AccountContactUpdate, AccountContactType, AccountService } from '../types/account';
import { OrderProduct } from '../types/order';
import apiService from '../services/api';
import {
  VM_ORDER_CATEGORY_SLUG,
  VM_ORDER_SUBCATEGORY_SLUG,
  resolveVmPlanDisplay,
} from '../utils/virtualMachinePlan';
import ServiceFormDialog from '../components/ServiceFormDialog';
import MoveToAccountDialog from '../components/MoveToAccountDialog';
import ServiceDetailView from '../components/ServiceDetailView';
import OutageDetailsModal from '../components/OutageDetailsModal';
import AccountFormDialog from '../components/AccountFormDialog';
import AccountCasesTab from '../components/AccountCasesTab';
import AccountAssetsTab from '../components/AccountAssetsTab';
import AccountBillingTab from '../components/AccountBillingTab';
import OpenCpanelButton, { canOpenCpanel } from '../components/OpenCpanelButton';
import AccountSupportDeskTab from '../components/AccountSupportDeskTab';
import AccountTasksTab from '../components/AccountTasksTab';
import AccountOrdersTab from '../components/AccountOrdersTab';
import AccountCommunicationsTab from '../components/AccountCommunicationsTab';
import AccountNotesTab from '../components/AccountNotesTab';
import AccountDocumentsTab from '../components/AccountDocumentsTab';
import AddressMiniMap from '../components/AddressMiniMap';
import { useAuth } from '../contexts/AuthContext';

const SERVICE_TYPE_LABELS: Record<string, string> = {
  nbn: 'NBN',
  mobile: 'Mobile',
  voice: 'Voice',
  sonweb: 'Sonweb',
  data: 'Data',
  supa_networks: 'Supa Networks',
};

/** MSP Agreement service type (code or display name from Settings → Service Types). */
function isMspAgreementServiceType(serviceTypeCode: string, serviceTypeName?: string | null): boolean {
  const c = (serviceTypeCode || '').trim().toLowerCase().replace(/-/g, '_');
  if (c === 'msp_agree' || c === 'msp_agreement') return true;
  const n = (serviceTypeName || '').trim().toLowerCase();
  return n === 'msp agreement';
}

/** First non-empty `type_specific_data` value for any of the given keys (exact or case-insensitive key match). */
function typeSpecificFirst(svc: AccountService, keys: string[]): string {
  const raw = svc.type_specific_data;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return '—';
  const obj = raw as Record<string, unknown>;
  const byLower = new Map<string, unknown>();
  for (const k of Object.keys(obj)) {
    byLower.set(k.toLowerCase(), obj[k]);
  }
  for (const k of keys) {
    let v = obj[k];
    if (v === undefined || v === null) v = byLower.get(k.toLowerCase());
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
  }
  return '—';
}

/** Settings → Service Types often stores select values as slugs like `msp_agree_per_duration_month`. */
function formatMspPerDurationOverview(raw: string): string {
  const s = (raw || '').trim();
  if (!s) return '—';
  const prefix = 'msp_agree_per_duration_';
  const lower = s.toLowerCase();
  if (lower.startsWith(prefix)) {
    const rest = s.slice(prefix.length);
    return rest
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
  if (!s.includes('_')) return s;
  return s
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const TAB_KEYS = ['profile', 'contacts', 'services', 'orders', 'assets', 'billing', 'cases', 'tasks', 'support', 'communications', 'notes', 'documents'] as const;
type TabValue = (typeof TAB_KEYS)[number];

interface TabPanelProps {
  value: TabValue;
  current: number;
  children: React.ReactNode;
}

function TabPanel({ value, current, children }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={TAB_KEYS[current] !== value}>
      {TAB_KEYS[current] === value && <Box sx={{ pt: 1.5 }}>{children}</Box>}
    </div>
  );
}

const CONTACT_TYPES: AccountContactType[] = ['primary', 'secondary', 'billing', 'admin', 'technical'];

function ProfileSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#1e3a5c', px: 1.5, py: 0.5, borderRadius: 1, mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function resolveAccountAddress(account: Account): string | null {
  if (account.address_formatted) return account.address_formatted;
  const streetLine = [account.address_line1, account.address_line2].filter(Boolean).join(', ');
  const parts = [streetLine, account.address_suburb, account.address_state, account.address_postcode].filter(Boolean);
  return parts.length > 0 ? `${parts.join(', ')}, ${account.address_country || 'Australia'}` : null;
}

interface ServiceOutageSummary {
  account_service_id: string;
  incident_id: string;
  title: string | null;
  status: string;
  planned: boolean | null;
  start_time: string | null;
  estimated_resolution: string | null;
  location_area: string | null;
}

const AccountDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const openServiceId = (location.state as { openServiceId?: string })?.openServiceId;
  const serviceIdFromQuery = searchParams.get('service');
  const assetIdFromQuery = searchParams.get('asset');
  const tabFromQuery = searchParams.get('tab');
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const [contacts, setContacts] = useState<AccountContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [isEditingContacts, setIsEditingContacts] = useState(false);
  const [contactViewOpen, setContactViewOpen] = useState(false);
  const [contactViewContact, setContactViewContact] = useState<AccountContact | null>(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactDialogMode, setContactDialogMode] = useState<'add' | 'edit'>('add');
  const [contactDialogContact, setContactDialogContact] = useState<AccountContact | null>(null);
  const [contactDialogSaving, setContactDialogSaving] = useState(false);
  const [contactDialogError, setContactDialogError] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState<AccountContactCreate>({
    first_name: '',
    last_name: '',
    email: '',
    main_phone: '',
    mobile_number: '',
    date_of_birth: '',
    contact_type: 'secondary',
  });

  const [services, setServices] = useState<AccountService[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceOutages, setServiceOutages] = useState<Record<string, ServiceOutageSummary[]>>({});
  const [outageModalIncident, setOutageModalIncident] = useState<string | null>(null);
  const [outageMenu, setOutageMenu] = useState<{ anchor: HTMLElement; items: ServiceOutageSummary[] } | null>(null);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceDialogService, setServiceDialogService] = useState<AccountService | null>(null);
  const [selectedServiceView, setSelectedServiceView] = useState<AccountService | null>(null);
  const [moveServiceOpen, setMoveServiceOpen] = useState(false);
  const [serviceToMove, setServiceToMove] = useState<AccountService | null>(null);
  const [servicesSuccess, setServicesSuccess] = useState<string | null>(null);
  const [profileFormOpen, setProfileFormOpen] = useState(false);
  const { user } = useAuth();
  const [viewAsCustomerLoading, setViewAsCustomerLoading] = useState(false);
  const [viewAsCustomerError, setViewAsCustomerError] = useState<string | null>(null);
  const [accountReviews, setAccountReviews] = useState<any[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [vmPlanProducts, setVmPlanProducts] = useState<OrderProduct[]>([]);
  /** Live MSP hours available (balances API) keyed by account_service id for Services overview */
  const [mspOverviewAvailById, setMspOverviewAvailById] = useState<Record<string, string | null>>({});
  const [mspOverviewAvailLoading, setMspOverviewAvailLoading] = useState(false);

  useEffect(() => {
    const hasVm = services.some((s) => (s.service_type || '').toLowerCase() === 'virtual_machine');
    if (!hasVm) {
      setVmPlanProducts([]);
      return;
    }
    let cancelled = false;
    apiService
      .getOrderProducts({
        category: VM_ORDER_CATEGORY_SLUG,
        subcategory: VM_ORDER_SUBCATEGORY_SLUG,
      })
      .then((rows) => {
        if (!cancelled) setVmPlanProducts(rows);
      })
      .catch(() => {
        if (!cancelled) setVmPlanProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [services]);

  useEffect(() => {
    const mspSvcs = services.filter((s) =>
      isMspAgreementServiceType(s.service_type || '', s.service_type_name)
    );
    if (!mspSvcs.length) {
      setMspOverviewAvailById({});
      setMspOverviewAvailLoading(false);
      return;
    }
    let cancelled = false;
    setMspOverviewAvailLoading(true);
    Promise.all(
      mspSvcs.map(async (s) => {
        try {
          const b = await apiService.getMspBalance(s.id);
          return [s.id, String(b.available_total)] as const;
        } catch {
          return [s.id, null] as const;
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      const map: Record<string, string | null> = {};
      for (const [sid, v] of rows) map[sid] = v;
      setMspOverviewAvailById(map);
    }).finally(() => {
      if (!cancelled) setMspOverviewAvailLoading(false);
    });
    return () => {
      cancelled = true;
      setMspOverviewAvailLoading(false);
    };
  }, [services]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getAccount(id);
        if (!cancelled) setAccount(data);
      } catch (e: unknown) {
        if (!cancelled) setError((e as Error)?.message || 'Failed to load account');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const loadAccountReviews = useCallback(async () => {
    if (!id) return;
    setReviewsLoading(true);
    try {
      const reviews = await apiService.adminGetAccountReviews(id);
      setAccountReviews(reviews);
    } catch {
      setAccountReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [id]);

  useEffect(() => { loadAccountReviews(); }, [loadAccountReviews]);

  const loadContacts = React.useCallback(async () => {
    if (!id) return;
    setContactsLoading(true);
    setContactsError(null);
    try {
      const { contacts: list } = await apiService.getAccountContacts(id);
      setContacts(list || []);
    } catch (e: unknown) {
      setContactsError((e as Error)?.message || 'Failed to load contacts');
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && tabIndex === TAB_KEYS.indexOf('contacts')) {
      loadContacts();
    }
  }, [id, tabIndex, loadContacts]);

  const loadServices = React.useCallback(async () => {
    if (!id) return;
    setServicesLoading(true);
    setServicesError(null);
    try {
      const res = await apiService.getAccountServices(id, { per_page: 500 });
      setServices(res.services || []);
      // Outage indicators are supplementary: a failure here must not break the services list
      try {
        const o = await apiService.getAccountServiceOutages(id);
        const byService: Record<string, ServiceOutageSummary[]> = {};
        (o.outages || []).forEach((x: ServiceOutageSummary) => {
          (byService[x.account_service_id] = byService[x.account_service_id] || []).push(x);
        });
        setServiceOutages(byService);
      } catch {
        setServiceOutages({});
      }
    } catch (e: unknown) {
      setServicesError((e as Error)?.message || 'Failed to load services');
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && openServiceId) {
      setTabIndex(TAB_KEYS.indexOf('services'));
    }
  }, [id, openServiceId]);

  useEffect(() => {
    if (!tabFromQuery) return;
    const key = tabFromQuery === 'accounting' ? 'billing' : tabFromQuery; // the Accounting tab was renamed Billing
    const idx = TAB_KEYS.indexOf(key as TabValue);
    if (idx >= 0) setTabIndex(idx);
  }, [tabFromQuery]);

  useEffect(() => {
    if (serviceIdFromQuery && id) {
      setTabIndex(TAB_KEYS.indexOf('services'));
    }
  }, [serviceIdFromQuery, id]);

  useEffect(() => {
    if (assetIdFromQuery && id) {
      setTabIndex(TAB_KEYS.indexOf('assets'));
    }
  }, [assetIdFromQuery, id]);

  useEffect(() => {
    if (id && tabIndex === TAB_KEYS.indexOf('services')) {
      loadServices();
    }
  }, [id, tabIndex, loadServices]);

  useEffect(() => {
    if (openServiceId && services.length > 0) {
      const svc = services.find((s) => s.id === openServiceId);
      if (svc) setSelectedServiceView(svc);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [openServiceId, services, navigate, location.pathname]);

  useEffect(() => {
    if (!serviceIdFromQuery || services.length === 0) return;
    const svc = services.find((s) => s.id === serviceIdFromQuery);
    if (svc) {
      setSelectedServiceView(svc);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('service');
          next.delete('tab');
          return next;
        },
        { replace: true }
      );
    }
  }, [serviceIdFromQuery, services, setSearchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    const key = TAB_KEYS[newValue];
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true }
    );
  };

  const openAddContact = () => {
    setContactDialogError(null);
    setContactForm({
      first_name: '',
      last_name: '',
      email: '',
      main_phone: '',
      mobile_number: '',
      date_of_birth: '',
      contact_type: 'secondary',
    });
    setContactDialogMode('add');
    setContactDialogContact(null);
    setContactDialogOpen(true);
  };

  const handleViewAsCustomer = async () => {
    if (!account) return;
    setViewAsCustomerLoading(true);
    setViewAsCustomerError(null);
    try {
      const { view_url } = await apiService.adminViewAccountAsCustomer(account.id);
      window.open(view_url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setViewAsCustomerError(e?.response?.data?.detail || e?.message || 'Could not open customer portal view');
    } finally {
      setViewAsCustomerLoading(false);
    }
  };

  const openEditContact = (contact: AccountContact) => {
    setContactDialogError(null);
    setContactForm({
      first_name: contact.first_name ?? '',
      last_name: contact.last_name ?? '',
      email: contact.email ?? '',
      main_phone: contact.main_phone ?? '',
      mobile_number: contact.mobile_number ?? '',
      date_of_birth: contact.date_of_birth ?? '',
      contact_type: contact.contact_type,
    });
    setContactDialogMode('edit');
    setContactDialogContact(contact);
    setContactDialogOpen(true);
  };

  const closeContactDialog = () => {
    setContactDialogOpen(false);
    setContactDialogContact(null);
    setContactDialogError(null);
  };

  const saveContactDialog = async () => {
    if (!id) return;
    setContactDialogError(null);
    setContactDialogSaving(true);
    try {
      if (contactDialogMode === 'add') {
        await apiService.createAccountContact(id, contactForm);
      } else if (contactDialogContact) {
        const updatePayload: AccountContactUpdate = {
          first_name: contactForm.first_name || undefined,
          last_name: contactForm.last_name || undefined,
          email: contactForm.email || undefined,
          main_phone: contactForm.main_phone || undefined,
          mobile_number: contactForm.mobile_number || undefined,
          date_of_birth: contactForm.date_of_birth || undefined,
          contact_type: contactForm.contact_type,
        };
        await apiService.updateAccountContact(id, contactDialogContact.id, updatePayload);
      }
      closeContactDialog();
      await loadContacts();
      setAccount(null);
      const acc = await apiService.getAccount(id);
      setAccount(acc);
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? (e as Error)?.message
        ?? 'Failed to save contact';
      setContactDialogError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setContactDialogSaving(false);
    }
  };

  const deleteContact = async (contact: AccountContact) => {
    if (!id || contact.contact_type === 'primary') return;
    try {
      await apiService.deleteAccountContact(id, contact.id);
      await loadContacts();
    } catch (_) {}
  };

  if (!id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Missing account ID</Alert>
        <IconButton onClick={() => navigate('/accounts')} sx={{ mt: 1 }}>
          <BackIcon /> Back to Accounts
        </IconButton>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !account) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Account not found'}</Alert>
        <IconButton onClick={() => navigate('/accounts')} sx={{ mt: 1 }}>
          <BackIcon /> Back to Accounts
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <IconButton onClick={() => navigate('/accounts')} size="small" title="Back to Accounts">
          <BackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" fontWeight={600}>
          {account.display_name}
        </Typography>
        {account.sonic_account_number && (
          <Typography variant="body2" color="text.secondary">
            #{account.sonic_account_number}
          </Typography>
        )}
        <Chip label={account.status} size="small" color={account.status === 'active' ? 'success' : 'default'} sx={{ ml: 1 }} />
      </Box>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
      >
        <Tab icon={<ProfileIcon />} iconPosition="start" label="Profile" />
        <Tab icon={<ContactsIcon />} iconPosition="start" label="Contacts" />
        <Tab icon={<ServicesIcon />} iconPosition="start" label="Services" />
        <Tab icon={<OrdersIcon />} iconPosition="start" label="Orders" />
        <Tab icon={<AssetsIcon />} iconPosition="start" label="Assets" />
        <Tab icon={<AccountingIcon />} iconPosition="start" label="Billing" />
        <Tab icon={<CasesIcon />} iconPosition="start" label="Cases" />
        <Tab icon={<TasksIcon />} iconPosition="start" label="Tasks" />
        <Tab icon={<TicketsIcon />} iconPosition="start" label="Support" />
        <Tab icon={<CommunicationsIcon />} iconPosition="start" label="Communications" />
        <Tab icon={<NotesIcon />} iconPosition="start" label="Notes" />
        <Tab icon={<DocumentsIcon />} iconPosition="start" label="Documents" />
      </Tabs>

      <TabPanel value="profile" current={tabIndex}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Left: Account profile */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                <Typography variant="h6" fontWeight={700} color="primary.main">Account Profile</Typography>
                <Button startIcon={<EditIcon />} variant="outlined" size="small" onClick={() => setProfileFormOpen(true)}>
                  Edit
                </Button>
              </Box>

              <Grid container spacing={3}>
                {/* Identity */}
                <Grid item xs={12} md={4}>
                  <ProfileSectionLabel>Identity</ProfileSectionLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <AccountNumberIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Account number</Typography>
                        <Typography variant="body2" fontWeight={500}>{account.sonic_account_number || '—'}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Account type</Typography>
                        <Chip
                          label={account.account_type === 'business' ? 'Business' : 'Consumer'}
                          size="small"
                          variant="outlined"
                          color={account.account_type === 'business' ? 'primary' : 'default'}
                          sx={{ mt: 0.25 }}
                        />
                      </Box>
                    </Box>
                    {account.account_type === 'business' && account.abn && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <AbnIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">ABN</Typography>
                          <Typography variant="body2" fontWeight={500}>{account.abn}</Typography>
                        </Box>
                      </Box>
                    )}
                    {account.organisation_name && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <OrganisationIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Organisation</Typography>
                          <Typography variant="body2" fontWeight={500}>{account.organisation_name}</Typography>
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <ProfileIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Primary contact</Typography>
                        <Typography variant="body2" fontWeight={500}>{account.full_name || '—'}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <StatusIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                        <Chip
                          label={account.status}
                          size="small"
                          color={account.status === 'active' ? 'success' : account.status === 'suspended' ? 'warning' : 'default'}
                          variant="outlined"
                          sx={{ mt: 0.25 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Contact */}
                <Grid item xs={12} md={4}>
                  <ProfileSectionLabel>Contact</ProfileSectionLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <AddressIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Address</Typography>
                        {(account.address_line1 || account.address_suburb || account.address_country) ? (
                          <Box>
                            {account.address_line1 && <Typography variant="body2">{account.address_line1}</Typography>}
                            {account.address_line2 && <Typography variant="body2">{account.address_line2}</Typography>}
                            {[account.address_suburb, account.address_state, account.address_postcode].filter(Boolean).length > 0 && (
                              <Typography variant="body2">{[account.address_suburb, account.address_state, account.address_postcode].filter(Boolean).join(' ')}</Typography>
                            )}
                            {account.address_country && <Typography variant="body2">{account.address_country}</Typography>}
                          </Box>
                        ) : account.address_formatted ? (
                          <Typography variant="body2">{account.address_formatted}</Typography>
                        ) : (
                          <Typography variant="body2">—</Typography>
                        )}
                      </Box>
                    </Box>
                    {resolveAccountAddress(account) && (
                      <AddressMiniMap address={resolveAccountAddress(account)} height={120} />
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
                        <Typography variant="body2">{account.main_phone || account.mobile_number || '—'}</Typography>
                        {account.main_phone && account.mobile_number && (
                          <Typography variant="body2" color="text.secondary">Mobile: {account.mobile_number}</Typography>
                        )}
                        {account.fax && (
                          <Typography variant="body2" color="text.secondary">Fax: {account.fax}</Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <CommunicationsIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                        <Typography variant="body2">{account.email || '—'}</Typography>
                      </Box>
                    </Box>
                    {account.website && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <WebsiteIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Website</Typography>
                          <Typography variant="body2">
                            <a href={account.website.startsWith('http') ? account.website : `https://${account.website}`} target="_blank" rel="noopener noreferrer">
                              {account.website}
                            </a>
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Preferences */}
                <Grid item xs={12} md={4}>
                  <ProfileSectionLabel>Preferences</ProfileSectionLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <NotificationsIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Notifications</Typography>
                        <Box display="flex" gap={0.5} mt={0.25} flexWrap="wrap">
                          <Chip label="Email" size="small" color={account.notification_preferences?.email ? 'success' : 'default'} variant={account.notification_preferences?.email ? 'filled' : 'outlined'} />
                          <Chip label="SMS" size="small" color={account.notification_preferences?.sms ? 'success' : 'default'} variant={account.notification_preferences?.sms ? 'filled' : 'outlined'} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {user?.is_admin && (
                    <Box sx={{ mt: 3 }}>
                      <ProfileSectionLabel>Customer Portal</ProfileSectionLabel>
                      <Button
                        startIcon={<ViewIcon />}
                        variant="outlined"
                        size="small"
                        disabled={viewAsCustomerLoading}
                        onClick={handleViewAsCustomer}
                      >
                        {viewAsCustomerLoading ? 'Opening…' : 'View as customer'}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Opens my.sonicnet.net.au as this customer, in a new tab. No notification is sent to the customer.
                      </Typography>
                      {viewAsCustomerError && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                          {viewAsCustomerError}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Right: Google Reviews */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <ReviewsIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700}>Google Reviews</Typography>
                {accountReviews.length > 0 && (
                  <Chip label={accountReviews.length} size="small" sx={{ ml: 'auto', bgcolor: '#f59e0b22', color: '#b45309', fontWeight: 700 }} />
                )}
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              {reviewsLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
              ) : accountReviews.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                  No linked reviews yet.<br />Link reviews from Marketing → Google Reviews.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {accountReviews.map((review: any, idx: number) => (
                    <Box key={review.review_id}>
                      {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <Avatar src={review.profile_photo_url} sx={{ width: 32, height: 32, flexShrink: 0 }}>
                          {(review.author_name || 'A')[0]}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" fontWeight={600} noWrap>{review.author_name || 'Anonymous'}</Typography>
                            {review.google_maps_uri && (
                              <Tooltip title="View on Google">
                                <IconButton size="small" href={review.google_maps_uri} target="_blank" rel="noopener noreferrer" sx={{ p: 0.25 }}>
                                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Box display="flex" gap={0.25} mb={0.5}>
                            {[1, 2, 3, 4, 5].map((n) =>
                              n <= (review.rating || 0)
                                ? <StarFilledIcon key={n} sx={{ fontSize: 13, color: '#f59e0b' }} />
                                : <StarBorderIcon key={n} sx={{ fontSize: 13, color: '#f59e0b' }} />
                            )}
                            {review.relative_time && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{review.relative_time}</Typography>
                            )}
                          </Box>
                          {review.text && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {review.text}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {account && (
          <AccountFormDialog
            open={profileFormOpen}
            onClose={() => setProfileFormOpen(false)}
            onSave={async (accountData) => {
              if (!id) return;
              await apiService.updateAccount(id, accountData);
              const updated = await apiService.getAccount(id);
              setAccount(updated);
              setProfileFormOpen(false);
            }}
            account={account}
          />
        )}
      </TabPanel>

      <TabPanel value="contacts" current={tabIndex}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="primary.main">
              Contacts
            </Typography>
            {!isEditingContacts ? (
              <Button startIcon={<EditIcon />} variant="outlined" size="small" onClick={() => setIsEditingContacts(true)}>
                Edit
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAddContact}>
                  Add contact
                </Button>
                <Button variant="outlined" size="small" onClick={() => setIsEditingContacts(false)}>
                  Done
                </Button>
              </Box>
            )}
          </Box>
          {contactsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : contactsError ? (
            <Alert severity="error" onClose={() => setContactsError(null)}>
              {contactsError}
            </Alert>
          ) : contacts.length === 0 ? (
            <Typography color="text.secondary">No contacts for this account.</Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Main phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date of Birth</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contact type</TableCell>
                    <TableCell align="right" />
                    {isEditingContacts && (
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id} hover>
                      <TableCell>{contact.full_name || '—'}</TableCell>
                      <TableCell>{contact.email || '—'}</TableCell>
                      <TableCell>{contact.main_phone || '—'}</TableCell>
                      <TableCell>{contact.mobile_number || '—'}</TableCell>
                      <TableCell>
                        {contact.date_of_birth
                          ? formatDate(new Date(contact.date_of_birth + 'T00:00:00'), 'dd MMM yyyy')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={contact.contact_type}
                          size="small"
                          color={contact.contact_type === 'primary' ? 'primary' : 'default'}
                          variant={contact.contact_type === 'primary' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => { setContactViewContact(contact); setContactViewOpen(true); }} title="View contact">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      {isEditingContacts && (
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditContact(contact)} title="Edit contact">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {contact.contact_type !== 'primary' && (
                            <IconButton size="small" onClick={() => deleteContact(contact)} title="Delete contact" color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Dialog open={contactDialogOpen} onClose={closeContactDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{contactDialogMode === 'add' ? 'Add contact' : 'Edit contact'}</DialogTitle>
          <DialogContent>
            {contactDialogError && (
              <Alert severity="error" onClose={() => setContactDialogError(null)} sx={{ mb: 2 }}>
                {contactDialogError}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First name"
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm((f) => ({ ...f, first_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last name"
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm((f) => ({ ...f, last_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Main phone"
                  value={contactForm.main_phone}
                  onChange={(e) => setContactForm((f) => ({ ...f, main_phone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  value={contactForm.mobile_number}
                  onChange={(e) => setContactForm((f) => ({ ...f, mobile_number: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={contactForm.date_of_birth || ''}
                  onChange={(e) => setContactForm((f) => ({ ...f, date_of_birth: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Contact type</InputLabel>
                  <Select
                    value={contactForm.contact_type}
                    label="Contact type"
                    onChange={(e) => setContactForm((f) => ({ ...f, contact_type: e.target.value as AccountContactType }))}
                  >
                    {CONTACT_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {contactForm.contact_type === 'primary' && contactDialogContact?.contact_type !== 'primary' && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    The current primary contact will be changed to secondary.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeContactDialog}>Cancel</Button>
            <Button variant="contained" onClick={saveContactDialog} disabled={contactDialogSaving}>
              {contactDialogSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={contactViewOpen} onClose={() => setContactViewOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Contact details</DialogTitle>
          <DialogContent>
            {contactViewContact && (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">First name</Typography>
                  <Typography variant="body1">{contactViewContact.first_name || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Last name</Typography>
                  <Typography variant="body1">{contactViewContact.last_name || '—'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                  <Typography variant="body1">{contactViewContact.email || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Main phone</Typography>
                  <Typography variant="body1">{contactViewContact.main_phone || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Mobile</Typography>
                  <Typography variant="body1">{contactViewContact.mobile_number || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Date of Birth</Typography>
                  <Typography variant="body1">
                    {contactViewContact.date_of_birth
                      ? formatDate(new Date(contactViewContact.date_of_birth + 'T00:00:00'), 'dd MMM yyyy')
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Contact type</Typography>
                  <Chip
                    label={contactViewContact.contact_type}
                    size="small"
                    color={contactViewContact.contact_type === 'primary' ? 'primary' : 'default'}
                    variant={contactViewContact.contact_type === 'primary' ? 'filled' : 'outlined'}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactViewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value="services" current={tabIndex}>
        {selectedServiceView ? (
          <ServiceDetailView
            service={selectedServiceView}
            onBack={() => setSelectedServiceView(null)}
            onEdit={() => {
              setServiceDialogService(selectedServiceView);
              setServiceDialogOpen(true);
            }}
            onMove={() => {
              setServiceToMove(selectedServiceView);
              setMoveServiceOpen(true);
            }}
          />
        ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {servicesSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setServicesSuccess(null)}>
              {servicesSuccess}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="primary.main">
              Services
            </Typography>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => { setServiceDialogService(null); setServiceDialogOpen(true); }}>
              Add service
            </Button>
          </Box>
          {servicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : servicesError ? (
            <Alert severity="error" onClose={() => setServicesError(null)}>
              {servicesError}
            </Alert>
          ) : services.length === 0 ? (
            <Typography color="text.secondary">No services for this account.</Typography>
          ) : (
            (() => {
              const byType = services.reduce<Record<string, AccountService[]>>((acc, svc) => {
                const t = svc.service_type || 'other';
                if (!acc[t]) acc[t] = [];
                acc[t].push(svc);
                return acc;
              }, {});
              const order = ['nbn', 'mobile', 'voice', 'sonweb', 'data', 'supa_networks'];
              const types = order.filter((t) => byType[t]).concat(
                Object.keys(byType).filter((t) => !order.includes(t))
              );
              return (
                <Box sx={{ '& > div:not(:last-child)': { mb: 3 } }}>
                  {types.map((st) => {
                    const isNbn = st === 'nbn';
                    const isSonweb = st === 'sonweb';
                    const isSupa = st === 'supa_networks';
                    const isVirtualMachine = st.toLowerCase() === 'virtual_machine';
                    const isCloudServices = st === 'cloud_services';
                    const isMspAgreement = isMspAgreementServiceType(st, byType[st]?.[0]?.service_type_name);
                    return (
                      <Box key={st}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                          {(byType[st]?.[0]?.service_type_name && String(byType[st][0].service_type_name).trim()) ||
                            SERVICE_TYPE_LABELS[st] ||
                            st}
                        </Typography>
                        <TableContainer>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Service ID</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                {isVirtualMachine && (
                                  <>
                                    <TableCell sx={{ fontWeight: 600 }}>Hostname</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>VM Plan</TableCell>
                                  </>
                                )}
                                {isNbn && <TableCell sx={{ fontWeight: 600 }}>Vanity Identifier</TableCell>}
                                {isMspAgreement && (
                                  <>
                                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Hours</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Per Duration</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Hours available</TableCell>
                                  </>
                                )}
                                {isCloudServices && (
                                  <>
                                    <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Subscription</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Expiry</TableCell>
                                  </>
                                )}
                                {isSupa && <TableCell sx={{ fontWeight: 600 }}>Service Address</TableCell>}
                                {!isMspAgreement && !isCloudServices && (
                                  <TableCell sx={{ fontWeight: 600 }}>Upstream Carrier</TableCell>
                                )}
                                {isNbn && <TableCell sx={{ fontWeight: 600 }}>WSM Service ID</TableCell>}
                                {isNbn && <TableCell sx={{ fontWeight: 600 }}>NBN Carrier</TableCell>}
                                {isNbn && <TableCell sx={{ fontWeight: 600 }}>NBN AVC</TableCell>}
                                {isSonweb && <TableCell sx={{ fontWeight: 600 }}>Primary Domain</TableCell>}
                                {isSonweb && <TableCell sx={{ fontWeight: 600 }}>Web Server</TableCell>}
                                {isSonweb && <TableCell sx={{ fontWeight: 600 }}>Web Hosting Plan</TableCell>}
                                {isSonweb && <TableCell sx={{ fontWeight: 600 }}>Panel Type</TableCell>}
                                <TableCell sx={{ fontWeight: 600 }} align="center">Outages</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {byType[st].map((svc) => (
                                <TableRow
                                  key={svc.id}
                                  hover
                                  onClick={() => setSelectedServiceView(svc)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell>{svc.service_id || '—'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={svc.status}
                                      size="small"
                                      color={svc.status === 'active' ? 'success' : svc.status === 'suspended' ? 'warning' : 'default'}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  {isVirtualMachine && (
                                    <>
                                      <TableCell>
                                        {typeSpecificFirst(svc, ['hostname', 'host_name', 'vm_hostname', 'host'])}
                                      </TableCell>
                                      <TableCell>
                                        {typeSpecificFirst(svc, [
                                          'ip_address',
                                          'ip',
                                          'vm_ip_address',
                                          'ipaddress',
                                          'ipv4',
                                        ])}
                                      </TableCell>
                                      <TableCell>
                                        {(() => {
                                          const raw = typeSpecificFirst(svc, [
                                            'vm_plan',
                                            'plan',
                                            'vmplan',
                                            'vm_plan_name',
                                          ]);
                                          return raw === '—'
                                            ? '—'
                                            : resolveVmPlanDisplay(raw, vmPlanProducts);
                                        })()}
                                      </TableCell>
                                    </>
                                  )}
                                  {isNbn && <TableCell>{svc.nbn_vanity_identifier || '—'}</TableCell>}
                                  {isMspAgreement && (
                                    <>
                                      <TableCell>
                                        {typeSpecificFirst(svc, [
                                          'msp_agree_name',
                                          'name',
                                          'agreement_name',
                                          'msp_agreement_name',
                                          'title',
                                        ])}
                                      </TableCell>
                                      <TableCell>
                                        {typeSpecificFirst(svc, ['msp_agree_hours', 'hours'])}
                                      </TableCell>
                                      <TableCell>
                                        {(() => {
                                          const raw = typeSpecificFirst(svc, [
                                            'msp_agree_per_duration',
                                            'per_duration',
                                            'per_duration_type',
                                            'duration_period',
                                          ]);
                                          return raw === '—' ? '—' : formatMspPerDurationOverview(raw);
                                        })()}
                                      </TableCell>
                                      <TableCell>
                                        {mspOverviewAvailLoading ? (
                                          <CircularProgress size={14} sx={{ verticalAlign: 'middle' }} />
                                        ) : mspOverviewAvailById[svc.id] != null ? (
                                          `${mspOverviewAvailById[svc.id]} h`
                                        ) : (
                                          '—'
                                        )}
                                      </TableCell>
                                    </>
                                  )}
                                  {isCloudServices && (
                                    <>
                                      <TableCell>{typeSpecificFirst(svc, ['cs_name'])}</TableCell>
                                      <TableCell>{typeSpecificFirst(svc, ['subscription'])}</TableCell>
                                      <TableCell>{typeSpecificFirst(svc, ['period'])}</TableCell>
                                      <TableCell>
                                        {(() => {
                                          const d = typeSpecificFirst(svc, ['next_expiration_date']);
                                          if (d === '—') return '—';
                                          const parsed = new Date(d);
                                          return isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                                        })()}
                                      </TableCell>
                                    </>
                                  )}
                                  {isSupa && <TableCell>{String((svc.type_specific_data as Record<string, unknown> | undefined)?.service_address || '—')}</TableCell>}
                                  {!isMspAgreement && !isCloudServices && <TableCell>{svc.upstream_carrier || '—'}</TableCell>}
                                  {isNbn && <TableCell>{svc.wsm_service_id || '—'}</TableCell>}
                                  {isNbn && <TableCell>{svc.nbn_carrier_id || '—'}</TableCell>}
                                  {isNbn && <TableCell>{svc.nbn_avc || '—'}</TableCell>}
                                  {isSonweb && <TableCell>{svc.primary_domain || '—'}</TableCell>}
                                  {isSonweb && <TableCell>{svc.web_server || '—'}</TableCell>}
                                  {isSonweb && <TableCell>{svc.web_hosting_plan || '—'}</TableCell>}
                                  {isSonweb && <TableCell>{svc.panel_type || '—'}</TableCell>}
                                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                    {(() => {
                                      const items = serviceOutages[svc.id] || [];
                                      if (items.length === 0) return null;
                                      const hasLive = items.some((i) => i.status !== 'planned');
                                      const label = items
                                        .map((i) => `${i.status === 'planned' ? 'Planned' : 'Outage'}: ${i.title || i.incident_id}`)
                                        .join('\n');
                                      return (
                                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{label}</span>}>
                                          <IconButton
                                            size="small"
                                            color={hasLive ? 'error' : 'info'}
                                            aria-label={hasLive ? 'View outage' : 'View planned outage'}
                                            onClick={(e) => {
                                              if (items.length === 1) setOutageModalIncident(items[0].incident_id);
                                              else setOutageMenu({ anchor: e.currentTarget, items });
                                            }}
                                          >
                                            {hasLive ? <ServiceOutageIcon fontSize="small" /> : <PlannedOutageIcon fontSize="small" />}
                                          </IconButton>
                                        </Tooltip>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    <IconButton size="small" onClick={() => setSelectedServiceView(svc)} title="View service">
                                      <ViewIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setServiceToMove(svc);
                                        setMoveServiceOpen(true);
                                      }}
                                      title="Move to another account"
                                    >
                                      <MoveServiceIcon fontSize="small" />
                                    </IconButton>
                                    {user?.is_admin && canOpenCpanel(svc) && <OpenCpanelButton serviceId={svc.id} variant="icon" />}
                                    <IconButton size="small" onClick={() => { setServiceDialogService(svc); setServiceDialogOpen(true); }} title="Edit service">
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()
          )}
        </Paper>
        )}
        {id && (
          <ServiceFormDialog
            open={serviceDialogOpen}
            onClose={() => { setServiceDialogOpen(false); setServiceDialogService(null); }}
            onSave={async () => {
              await loadServices();
              const editedId = serviceDialogService?.id;
              const viewedId = selectedServiceView?.id;
              if (editedId && viewedId === editedId && id) {
                const res = await apiService.getAccountServices(id, { per_page: 500 });
                const svcs: AccountService[] = res.services ?? [];
                const updated = svcs.find((s) => s.id === editedId);
                if (updated) setSelectedServiceView(updated);
              }
            }}
            accountId={id}
            service={serviceDialogService}
            onRequestMove={
              serviceDialogService
                ? () => {
                    const svc = serviceDialogService;
                    setServiceDialogOpen(false);
                    setServiceDialogService(null);
                    setServiceToMove(svc);
                    setMoveServiceOpen(true);
                  }
                : undefined
            }
          />
        )}
        {id && (
          <MoveToAccountDialog
            open={moveServiceOpen}
            onClose={() => {
              setMoveServiceOpen(false);
              setServiceToMove(null);
            }}
            excludeAccountId={id}
            title="Move service to another account"
            subtitle={serviceToMove ? `Service ID: ${serviceToMove.service_id}` : undefined}
            onTransferred={async (targetAccountId) => {
              if (!serviceToMove) return;
              await apiService.transferAccountService(id, serviceToMove.id, targetAccountId);
              await loadServices();
              setSelectedServiceView(null);
              setMoveServiceOpen(false);
              setServiceToMove(null);
              setServicesSuccess('Service moved to the selected account.');
            }}
          />
        )}
      </TabPanel>

      <TabPanel value="orders" current={tabIndex}>
        {id ? <AccountOrdersTab accountId={id} /> : null}
      </TabPanel>

      <TabPanel value="assets" current={tabIndex}>
        <AccountAssetsTab
          accountId={id || ''}
          onError={setError}
          openAssetIdFromQuery={assetIdFromQuery}
          onOpenAssetConsumed={() => {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('asset');
                next.delete('tab');
                return next;
              },
              { replace: true }
            );
          }}
        />
      </TabPanel>

      <TabPanel value="billing" current={tabIndex}>
        <AccountBillingTab accountId={id || ''} />
      </TabPanel>

      <TabPanel value="cases" current={tabIndex}>
        <AccountCasesTab accountId={id || ''} navigate={navigate} />
      </TabPanel>

      <TabPanel value="tasks" current={tabIndex}>
        {id ? <AccountTasksTab accountId={id} /> : null}
      </TabPanel>

      <TabPanel value="support" current={tabIndex}>
        {id ? <AccountSupportDeskTab accountId={id} /> : null}
      </TabPanel>

      <TabPanel value="communications" current={tabIndex}>
        {id && account ? (
          <AccountCommunicationsTab
            accountId={id}
            mobileNumber={account.mobile_number}
            customerName={account.display_name}
            contacts={contacts}
          />
        ) : null}
      </TabPanel>

      <TabPanel value="notes" current={tabIndex}>
        {id ? <AccountNotesTab accountId={id} /> : null}
      </TabPanel>

      <TabPanel value="documents" current={tabIndex}>
        {id ? <AccountDocumentsTab accountId={id} /> : null}
      </TabPanel>

      <Menu
        anchorEl={outageMenu?.anchor || null}
        open={Boolean(outageMenu)}
        onClose={() => setOutageMenu(null)}
      >
        {(outageMenu?.items || []).map((o) => (
          <MenuItem
            key={o.incident_id}
            onClick={() => {
              setOutageModalIncident(o.incident_id);
              setOutageMenu(null);
            }}
          >
            {o.status === 'planned' ? 'Planned' : 'Outage'}: {o.title || o.incident_id}
          </MenuItem>
        ))}
      </Menu>

      <OutageDetailsModal
        open={Boolean(outageModalIncident)}
        onClose={() => setOutageModalIncident(null)}
        incidentId={outageModalIncident || ''}
      />
    </Box>
  );
};

export default AccountDetailPage;
